import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';
const BG_IMAGE = 'https://images.unsplash.com/photo-1772723246543-213f2a4fc526?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMGRpdmVyc2UlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY1NjA1Mjd8MA&ixlib=rb-4.1.0&q=85';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      const message = formatApiError(error.response?.data?.detail) || error.message;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 py-12 bg-[#FDFBF7]">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="mb-10">
            <img src={LOGO_URL} alt="Kinsy" className="h-10" />
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">
              Welcome back
            </h1>
            <p className="text-stone-500">
              Sign in to continue to your family circle
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-stone-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500 pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium text-base"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-teal-600 hover:text-teal-700 font-medium" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </p>

          {/* Register Link */}
          <p className="mt-8 text-center text-stone-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-teal-600 hover:text-teal-700 font-medium" data-testid="register-link">
              Create one
            </Link>
          </p>
          
          <p className="mt-6 text-center text-xs text-stone-400">
            <Link to="/privacy" className="hover:text-stone-600">Privacy</Link>
            <span className="mx-2">·</span>
            <Link to="/terms" className="hover:text-stone-600">Terms</Link>
            <span className="mx-2">·</span>
            <Link to="/pricing" className="hover:text-stone-600">Pricing</Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600/20 to-transparent z-10" />
        <img
          src={BG_IMAGE}
          alt="Happy family"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-xl">
            <p className="text-xl font-medium text-stone-900 mb-2">
              "Kinsy brought our family closer together, even across different time zones."
            </p>
            <p className="text-stone-500">— The Johnson Family</p>
          </div>
        </div>
      </div>
    </div>
  );
}
